import React, { useCallback, useEffect, useReducer, useMemo, useRef } from 'react';
import axios from 'axios';
import { makeCancelTokenSource, getApiClient, handleApiError } from './api_client/get';
import ZendeskProvider, { useZendesk } from './contexts/zendesk';
import { googleTrackLogin, googleSetUserId } from './tracking/google';
import { User } from '@lemonadefashion/user';
import { AuthTokenType } from '@lemonadefashion/auth-token-type';
import { toast, ToastContainer } from 'react-toastify';
import ScrollToTop from './scroll_to_top';
import { clearListRestorationEntries } from './utils/list_restoration';
import AppRoutes from './routes';
import Popups from './components/popups/web_popup_delegate';
import FixedNavbar from './components/fixed_navbar/fixed_navbar';
import AnnouncementBanner from './components/banners/announcement';
import LazyRecaptchaProxy from './recaptcha/recaptcha';
import FullPageSpinner from './components/spinner/full_page_spinner';
import AppErrorBoundary from './error_boundary';
import BounceFix from './utils/bounceFix';
import I18nContext from './contexts/i18n';
import { BranchIOProvider } from './contexts/branch_io_provider';
import SellersContext from './contexts/sellers';
import RecaptchaContext from './contexts/recaptcha';
import FavoritesContext from './contexts/favorites';
import ShownPopupsContext from './contexts/popups_context';
import LogoAnimationContext from './contexts/logo_animation';
import CheckoutInfoContext from './contexts/checkout_info';
import ReferralHandler from './tracking/referral_handler';
import RTLController from './components/common/rtl_controller';
import { CookieConsentProvider } from './contexts/cookies';
import CartContext from './contexts/cart';
import AuthContext from './contexts/auth';
import GuestAccessCodeContext from './contexts/guest_access_code';
import ThemeProvider from './contexts/theme_provider';
import { WalletProvider } from './contexts/wallet';
import WalletModal from './components/wallet/wallet_modal';
import UserContext from './contexts/user';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { initialOptions } from './components/paypal/initialOptions';
import { TabbyScriptProvider } from './contexts/tabbyScriptProvider';
import { TabbyInfoPopupScriptProvider } from './contexts/tabbyInfoPopupScriptProvider';
import { TabbyCheckoutCardProvider } from './contexts/tabbyCheckoutCardScriptProvider';
import LatestTransactionIdContext from './contexts/latest_transaction_id';
import { MixpanelProvider, useMixpanel } from 'react-mixpanel-browser';
import { mixPanelSetUserId, mixPanelTrackLogin } from './tracking/mixpanel';
import ActivationContext from './contexts/activation';
import { ActivationFlagProvider } from './contexts/activation_flag';

import { Amplify, Auth, Hub } from 'aws-amplify';
import awsconfig from './aws-exports';
import { DownloadAppPopupProvider } from './contexts/download_app_popup_context';
import { TargetsContextProvider } from './contexts/targets_context';
import { TokenTranslationProvider } from './contexts/token_translation_context';
import { SavedAddressesProvider } from './contexts/saved_addresses';
import { PhoneNumbersProvider } from './contexts/phone_numbers';
import { ExperiencesProvider } from './contexts/experiences';
import { clearRememberedAuthRedirectPath, getRememberedAuthRedirectPath } from './utils/auth_redirect';

const environmentRedirectedURL = process.env.REACT_APP_COGNITO_REDIRECTED_URL;
const redirectSignInURL = awsconfig.oauth.redirectSignIn;
const firstRedirectURL = redirectSignInURL.split(',')[0].trim();

awsconfig.oauth.redirectSignIn = environmentRedirectedURL || firstRedirectURL;
if (process.env.REACT_APP_COGNITO_DOMAIN) {
  awsconfig.oauth.domain = process.env.REACT_APP_COGNITO_DOMAIN;
}

// Configure token refresh
Auth.configure({
  Auth: {
    refreshHandlers: {
      // Custom handler to refresh the token
      customTokenRefresh: async (user) => {
        try {
          const refreshedSession = await user.refreshSession();
          return refreshedSession.getIdToken().getJwtToken();
        } catch (error) {
          console.error('Error refreshing token:', error);
        }
      }
    }
  }
});

Amplify.configure(awsconfig);

const reducer = (state, action) => {
  switch (action.type) {
    case 'clear-user': return { ...state, user: null };
    case 'login-start': return { ...state, loggingIn: true };
    case 'login-success': return { ...state, loggingIn: false, user: action.user, isVerified: action.isVerified };
    case 'login-failure': return { ...state, loggingIn: false };
    case 'set-user': return { ...state, user: action.user, isVerified: action.isVerified };
    default: throw new Error('Unhandled action: ' + action.type);
  }
};

const syncAxiosAuthHeader = (authTokenContainer) => {
  const activeToken = authTokenContainer.getActiveToken();
  const activeTokenType = authTokenContainer.getActiveTokenType();
  if (activeToken != null && activeTokenType != null) {
    axios.defaults.headers.common.Authorization = `Bearer ${activeTokenType}${activeToken}`;
  } else {
    axios.defaults.headers.common.Authorization = '';
  }
};

const forgetCurrentAuthToken = (authTokenContainer, syncAxios = true) => {
  authTokenContainer.clearActiveToken().catch(error => console.log(error));
  if (syncAxios) syncAxiosAuthHeader(authTokenContainer);
};

const onInitError = error => {
  handleApiError(error, msg => {
    if (msg) toast.error(msg, { toastId: `app-initialization-error-${msg}` });
  });
};

const App = (props) => {
  return (
    <AppErrorBoundary>
      <MixpanelProvider>
        <ZendeskProvider>
          <InnerApp {...props} />
        </ZendeskProvider>
      </MixpanelProvider>
    </AppErrorBoundary>
  );
};

const InnerApp = ({
  history,
  authTokenContainer,
  cart,
  checkoutInfoContainer,
  sellersContainer,
  favoritesContainer,
  i18nSettings,
  shownPopupsContainer,
  guestAccessCodeContainer,
  latestTransactionIdContainer,
  lastTimeLogoAnimationPlayedContainer,
  ActivationContainer
}) => {
  const [state, dispatch] = useReducer(reducer, {
    user: null,
    recaptcha: new LazyRecaptchaProxy(),
    loggingIn: false,
    isVerified: false
  });
  const { user, recaptcha, loggingIn, isVerified } = state;
  const { setLocale: setZendeskLocale, refreshIdentity } = useZendesk();
  const mixPanel = useMixpanel();
  const locale = i18nSettings.getLocale();

  const applyRememberedAuthRedirect = useCallback(() => {
    const redirectPath = getRememberedAuthRedirectPath();
    if (!redirectPath) return false;
    const currentPath = `${history.location.pathname}${history.location.search}`;
    if (currentPath === redirectPath) {
      clearRememberedAuthRedirectPath();
      return false;
    }
    clearRememberedAuthRedirectPath();
    history.replace(redirectPath);
    return true;
  }, [history]);

  // TODO(moe): This should be done in an AuthProvider context.
  useEffect(() => {
    // Ensure Axios' Authorization header is synced with the tokens
    // we have in persistent local storage.
    syncAxiosAuthHeader(authTokenContainer);
  }, [authTokenContainer]);

  useEffect(() => {
    setZendeskLocale(locale);
  }, [locale, setZendeskLocale]);

  const kickUserOut = useCallback(() => {
    clearListRestorationEntries();
    dispatch({ type: 'clear-user' });
    forgetCurrentAuthToken(authTokenContainer);
    favoritesContainer.forceRefresh()
      .catch(error => handleApiError(error, msg => { if (msg) toast.error(msg); }));
  }, [authTokenContainer, favoritesContainer]);

  const downloadUserDataIfLoggedIn = useCallback((cancelTokenSource) => {
    const authToken = authTokenContainer.getActiveToken();
    if (authToken == null) {
      dispatch({ type: 'clear-user' });
      refreshIdentity();
    } else {
      dispatch({ type: 'login-start' });
      getApiClient().getCurrentUser(cancelTokenSource)
        .then(({ data }) => {
          if (data.success) {
            const newUser = new User(data.me);
            const isVerified = newUser.isVerified();
            dispatch({ type: 'login-success', user: newUser, isVerified: isVerified });
            googleSetUserId(newUser.getId());
            mixPanelSetUserId(mixPanel, newUser.getId());
            refreshIdentity();
          } else {
            dispatch({ type: 'login-failure' });
            console.log('Failed to get user data: ' + data.message);
          }
        })
        .catch(error => {
          dispatch({ type: 'login-failure' });
          handleApiError(error, console.log, kickUserOut);
        });
    }
  }, [authTokenContainer, kickUserOut, mixPanel, refreshIdentity]);

  const scrollTop = useRef(null);

  const fetchUserData = useCallback(async () => {
    try {
      const currentUser = await Auth.currentAuthenticatedUser({
        bypassCache: true
      });

      const currentSession = await Auth.currentSession();

      // Extract the refreshed tokens
      const idToken = currentSession.getIdToken().getJwtToken();

      // Set the authorization header for axios requests
      axios.defaults.headers.common.Authorization = `Bearer ${idToken}`;

      // Call your API to get user data
      const responseApi = await getApiClient().getCurrentUser(idToken);

      if (responseApi.data && responseApi.data.success) {
        console.log('Cognito login success!');
        await getApiClient().importSessionCart(idToken);
        if (!cart.isEphemeralCartActive()) {
          cart.forceRefresh()
              .catch(error => handleApiError(error, msg => { if (msg) toast.error(msg); }));
        }
        authTokenContainer.setActiveToken('', idToken)
          .catch(error => console.log(error));
        syncAxiosAuthHeader(authTokenContainer);
        const newUser = new User(responseApi.data.me);
        dispatch({ type: 'set-user', user: newUser, isVerified: newUser.isVerified() });
        // Set user IDs for tracking
        googleSetUserId(newUser.getId());
        mixPanelSetUserId(mixPanel, newUser.getId());
        refreshIdentity();
        return {
          idToken: idToken,
          currentUser: currentUser,
          apiResponse: responseApi.data
        };
      } else {
        console.log('Failed to get user data from API.');
        dispatch({ type: 'login-failure' });
        refreshIdentity();
        return null;
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      dispatch({ type: 'login-failure' });
      refreshIdentity();
      return null;
    }
  }, [authTokenContainer, dispatch, mixPanel, cart, refreshIdentity]);

  useEffect(() => {
    const listener = Hub.listen('auth', async (data) => {
      const { payload } = data;
      if (payload.event === 'signIn') {
        try {
          dispatch({ type: 'login-start' });
          const redirected = applyRememberedAuthRedirect();
          if (redirected) {
            fetchUserData().catch(error => {
              console.error('Error during fetchUserData:', error);
            });
            return;
          }
          const result = await fetchUserData();
          if (!result) return;
        } catch (error) {
          console.error('Error during fetchUserData:', error);
        }
      }
    });
    return () => Hub.remove('auth', listener);
  }, [applyRememberedAuthRedirect, fetchUserData, isVerified]);

  useEffect(() => {
    if (!user) return;
    applyRememberedAuthRedirect();
  }, [user, applyRememberedAuthRedirect]);

  useEffect(() => { cart.initialize().catch(onInitError); }, [cart]);
  useEffect(() => { i18nSettings.initialize().catch(onInitError); }, [i18nSettings]);
  useEffect(() => { favoritesContainer.initialize().catch(onInitError); }, [favoritesContainer]);
  useEffect(() => { sellersContainer.initialize().catch(onInitError); }, [sellersContainer]);
  // If we have an active logged in user, we should download logged in user data.
  useEffect(() => {
    const cancelTokenSource = makeCancelTokenSource();
    downloadUserDataIfLoggedIn(cancelTokenSource);
    return () => cancelTokenSource.cancel();
  }, [authTokenContainer, downloadUserDataIfLoggedIn]);

  // This will update the user data (in particular, information like credits)
  // upon explicit currency changes by the user.
  const currencyChangeCounter = i18nSettings.getCurrencyChangeCounter();
  useEffect(() => {
    // We will skip doing this if the currency was never changed explicitly in the current session.
    if (currencyChangeCounter === 0) return;
    const cancelTokenSource = makeCancelTokenSource();
    downloadUserDataIfLoggedIn(cancelTokenSource);
    return () => cancelTokenSource.cancel();
  }, [currencyChangeCounter, downloadUserDataIfLoggedIn]);

  const lemonadeLogin = useCallback((lfAuthToken, me) => {
    clearListRestorationEntries();
    googleTrackLogin('lemonade');
    mixPanelTrackLogin(mixPanel, 'lemonade');
    // First, ensure there are no stale tokens. This also ensures a stale token is invalidated.
    forgetCurrentAuthToken(authTokenContainer);
    // Now, properly set everything up.
    authTokenContainer.setActiveToken(AuthTokenType.LEMONADE_JWT, lfAuthToken).catch(error => console.log(error));
    syncAxiosAuthHeader(authTokenContainer);
    const newUser = new User(me);
    dispatch({ type: 'set-user', user: newUser, isVerified: newUser.isVerified() });
    favoritesContainer.forceRefresh().catch(error => handleApiError(error, msg => { if (msg) toast.error(msg); }));
    googleSetUserId(newUser.getId());
    refreshIdentity();
  }, [authTokenContainer, favoritesContainer, mixPanel, refreshIdentity]);

  const generalLogout = useCallback(() => {
    // Clear user data, then log out properly from Facebook if needed,
    // and finally, forget all auth tokens.
    clearListRestorationEntries();
    dispatch({ type: 'clear-user' });
    forgetCurrentAuthToken(authTokenContainer);
    cart.forceRefresh()
      .catch(error => handleApiError(error, msg => { if (msg) toast.error(msg); }));
    favoritesContainer.forceRefresh()
      .catch(error => handleApiError(error, msg => { if (msg) toast.error(msg); }));
    refreshIdentity();
  }, [authTokenContainer, favoritesContainer, cart, refreshIdentity]);

  const loggedIn = !!user;
  const userRelatedContext = useMemo(() => ({
    user,
    loggedIn,
    loggingIn,
    kickUserOut,
    logout: generalLogout,
    refreshUser: downloadUserDataIfLoggedIn,
    onLogin: lemonadeLogin,
    isVerified
  }), [user, loggedIn, loggingIn, kickUserOut, generalLogout, downloadUserDataIfLoggedIn, lemonadeLogin, isVerified
  ]);

  return (
    <AuthContext.Provider value={authTokenContainer}>
      <ThemeProvider apiClient={getApiClient()}>
        <RecaptchaContext.Provider value={recaptcha}>
          <GuestAccessCodeContext.Provider value={guestAccessCodeContainer}>
            <LogoAnimationContext.Provider value={lastTimeLogoAnimationPlayedContainer}>
              <I18nContext.Provider value={i18nSettings}>
                <TokenTranslationProvider>
                  <CookieConsentProvider>
                    <ActivationFlagProvider>
                      <ActivationContext.Provider value={ActivationContainer}>
                        <BranchIOProvider>
                          <TabbyInfoPopupScriptProvider>
                            <TabbyScriptProvider>
                              <TabbyCheckoutCardProvider>
                                <PayPalScriptProvider deferLoading options={initialOptions(i18nSettings.getCurrency())}>
                                  <UserContext.Provider value={userRelatedContext}>
                                    <FavoritesContext.Provider value={favoritesContainer}>
                                      <SellersContext.Provider value={sellersContainer}>
                                        <ShownPopupsContext.Provider value={shownPopupsContainer}>
                                          <DownloadAppPopupProvider>
                                            <TargetsContextProvider>
                                              <CartContext.Provider value={cart}>
                                                <PhoneNumbersProvider>
                                                  <SavedAddressesProvider>
                                                  <ExperiencesProvider>
                                                    <CheckoutInfoContext.Provider value={checkoutInfoContainer}>
                                                    {/* eslint-disable react/jsx-first-prop-new-line */}
                                                      <LatestTransactionIdContext.Provider
                                                        value={latestTransactionIdContainer}>
                                                        {/* eslint-disable react/jsx-max-depth */}
                                                        <ReferralHandler />
                                                        <RTLController />
                                                        <React.Suspense fallback={<FullPageSpinner />}>
                                                          <WalletProvider>
                                                          <BounceFix>
                                                            <FixedNavbar />
                                                            <WalletModal />
                                                            <Popups />
                                                            <React.Suspense fallback={<FullPageSpinner />}>
                                                              <ScrollToTop scrollToElement={scrollTop.current}>
                                                                <div ref={scrollTop} />
                                                                <div>
                                                                  <AnnouncementBanner />
                                                                  <AppRoutes />
                                                                </div>
                                                              </ScrollToTop>
                                                            </React.Suspense>
                                                          </BounceFix>
                                                          </WalletProvider>
                                                          <ToastContainer position='bottom-right' autoClose={5000}
                                                            hideProgressBar newestOnTop closeOnClick rtl={false}
                                                            pauseOnFocusLoss draggable pauseOnHover limit={3} />
                                                        </React.Suspense>
                                                      </LatestTransactionIdContext.Provider>
                                                    </CheckoutInfoContext.Provider>
                                                  </ExperiencesProvider>
                                                  </SavedAddressesProvider>
                                                </PhoneNumbersProvider>
                                              </CartContext.Provider>
                                            </TargetsContextProvider>
                                          </DownloadAppPopupProvider>
                                        </ShownPopupsContext.Provider>
                                      </SellersContext.Provider>
                                    </FavoritesContext.Provider>
                                  </UserContext.Provider>
                                </PayPalScriptProvider>
                              </TabbyCheckoutCardProvider>
                            </TabbyScriptProvider>
                          </TabbyInfoPopupScriptProvider>
                        </BranchIOProvider>
                      </ActivationContext.Provider>
                    </ActivationFlagProvider>
                  </CookieConsentProvider>
                </TokenTranslationProvider>
              </I18nContext.Provider>
            </LogoAnimationContext.Provider>
          </GuestAccessCodeContext.Provider>
        </RecaptchaContext.Provider>
      </ThemeProvider>
    </AuthContext.Provider>
  );
};

export default App;
